import { InteractionType, type RedirectRequest } from '@azure/msal-browser';
import {
  MsalAuthenticationTemplate,
  UnauthenticatedTemplate,
} from '@azure/msal-react';
import {
  Bot24Filled,
  Bot24Regular,
  Database24Filled,
  Database24Regular,
  Home24Filled,
  Home24Regular,
  Settings24Filled,
  Settings24Regular,
  Beaker24Filled,
  Beaker24Regular,
  BrainCircuit24Filled,
  BrainCircuit24Regular,
  ClipboardTaskListLtr24Filled,
  ClipboardTaskListLtr24Regular,
  DataTrending24Filled,
  DataTrending24Regular,
  DocumentRibbon24Filled,
  DocumentRibbon24Regular,
  Gavel24Filled,
  Gavel24Regular,
  ShareMultiple24Filled,
  ShareMultiple24Regular,
  ShieldLock24Filled,
  ShieldLock24Regular,
  bundleIcon,
} from '@fluentui/react-icons';
import { useEffect, useMemo, useRef } from 'react';
import { Outlet, useLocation, useMatches } from 'react-router';

import { HttpClientProvider } from './api/HttpClientContext';
import { createAuthRedirectState } from './auth/authRedirectState';
import { loginRequest } from './auth/msalConfig';
import { AppShell, UserMenu } from './components/app-shell';
import type { NavSectionData } from './components/app-shell';
import { ErrorBoundary } from './components/ErrorBoundary/ErrorBoundary';
import { LoadingSpinner } from './components/LoadingSpinner/LoadingSpinner';
import { WorkspaceDrawer } from './components/WorkspaceDrawer/WorkspaceDrawer';
import { WorkspaceSwitcher } from './components/WorkspaceSwitcher/WorkspaceSwitcher';
import { config } from './config';
import { useAppContext } from './context/AppContext';
import {
  WorkspaceProvider,
  useWorkspaceContext,
} from './context/WorkspaceContext';
import { WorkspaceDrawerProvider } from './context/WorkspaceDrawerContext';
import { getJudgeFactoryPath } from './features/judge-factory';
import { useAuth } from './hooks/useAuth/useAuth';
import { useServiceVersion } from './hooks/useServiceVersion/useServiceVersion';
import { useWorkspaces } from './hooks/useWorkspaces/useWorkspaces';
import { RouteTelemetryTracker } from './telemetry/components/RouteTelemetryTracker/RouteTelemetryTracker.js';

/* eslint-disable @typescript-eslint/no-unsafe-assignment */
const HomeIcon = bundleIcon(Home24Filled, Home24Regular);
const JudgeIcon = bundleIcon(Beaker24Filled, Beaker24Regular);
const JudgeFactoryIcon = bundleIcon(Gavel24Filled, Gavel24Regular);
const AgentsIcon = bundleIcon(Bot24Filled, Bot24Regular);
const DataIcon = bundleIcon(Database24Filled, Database24Regular);
const AgentIcon = bundleIcon(BrainCircuit24Filled, BrainCircuit24Regular);
const SettingsIcon = bundleIcon(Settings24Filled, Settings24Regular);
const EvalRunsIcon = bundleIcon(
  ClipboardTaskListLtr24Filled,
  ClipboardTaskListLtr24Regular,
);
const TrendsIcon = bundleIcon(DataTrending24Filled, DataTrending24Regular);
const PermissionsIcon = bundleIcon(ShieldLock24Filled, ShieldLock24Regular);
const SharedSignalsIcon = bundleIcon(
  ShareMultiple24Filled,
  ShareMultiple24Regular,
);
const EvalTemplateIcon = bundleIcon(
  DocumentRibbon24Filled,
  DocumentRibbon24Regular,
);
/* eslint-enable @typescript-eslint/no-unsafe-assignment */

function buildNavSections(
  workspaceName: string | null,
  workspaceApiName: string | null,
): NavSectionData[] {
  const sections: NavSectionData[] = [
    {
      header: 'GENERAL',
      items: [
        {
          key: 'home',
          label: 'Home',
          description: 'Welcome to AI Studio',
          icon: <HomeIcon />,
          path: '/',
          end: true,
        },
      ],
    },
  ];

  if (workspaceName && workspaceApiName) {
    sections.push({
      header: workspaceName,
      items: [
        {
          key: 'agents',
          label: 'Agents',
          description: 'Create and manage agents under evaluation',
          icon: <AgentsIcon />,
          path: `/workspaces/${encodeURIComponent(workspaceApiName)}/agents`,
        },
        {
          key: 'graders',
          label: 'Graders',
          description: 'Create and manage evaluation graders',
          icon: <JudgeIcon />,
          path: `/workspaces/${encodeURIComponent(workspaceApiName)}/graders`,
          children: [
            {
              key: 'judge-factory-gen',
              label: 'Judge Factory',
              description: 'Generate judges',
              icon: <JudgeFactoryIcon />,
              path: getJudgeFactoryPath(workspaceApiName),
            },
          ],
        },
        {
          key: 'evaluation-templates',
          label: 'Eval Templates',
          description: 'Create and manage evaluation templates',
          icon: <EvalTemplateIcon />,
          path: `/workspaces/${encodeURIComponent(workspaceApiName)}/evaluation-templates`,
        },
        {
          key: 'eval-runs',
          label: 'Eval Runs',
          description: 'Track and launch eval runs',
          icon: <EvalRunsIcon />,
          path: `/workspaces/${encodeURIComponent(workspaceApiName)}/eval-runs`,
        },
        {
          key: 'trends',
          label: 'Trends',
          description: 'View evaluation trends and reporting',
          icon: <TrendsIcon />,
          path: `/workspaces/${encodeURIComponent(workspaceApiName)}/trends`,
        },
      ],
    });
  }

  sections.push(
    {
      header: 'DATALAKE',
      items: [
        {
          key: 'datasets',
          label: 'Datasets',
          description: 'Browse and manage datasets',
          icon: <DataIcon />,
          path: '/datasets',
        },
        {
          key: 'testsets',
          label: 'Test Sets',
          description: 'Browse and manage evaluation test sets',
          icon: <DataIcon />,
          path: '/testsets',
        },
        {
          key: 'permissions',
          label: 'Permissions',
          description: 'Manage tenant group role assignments',
          icon: <PermissionsIcon />,
          path: '/permissions',
        },
      ],
    },
    {
      header: 'TOOLS',
      items: [
        {
          key: 'debug',
          label: 'Agent Debug',
          description: 'Debug and inspect agent interactions',
          icon: <AgentIcon />,
          path: '/debug',
        },
        {
          key: 'shared-signals',
          label: 'Shared Signals',
          description: 'View and manage shared signals',
          icon: <SharedSignalsIcon />,
          path: '/shared-signals',
        },
        {
          key: 'caller',
          label: 'Caller',
          description: 'Test API endpoints',
          icon: <SettingsIcon />,
          path: '/caller',
        },
      ],
    },
  );

  return sections;
}

export function AuthErrorMessage({ error }: { error: unknown }) {
  const message =
    error instanceof Error ? error.message : 'An authentication error occurred';
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
      }}
    >
      <p>Sign-in error: {message}</p>
    </div>
  );
}

function AuthLoadingSpinner() {
  return (
    <div style={{ height: '100vh' }}>
      <LoadingSpinner label="Signing in..." />
    </div>
  );
}

function AppContent() {
  useAppContext();
  const { pathname } = useLocation();
  const matches = useMatches();
  const { account, signOut } = useAuth();
  const serviceVersion = useServiceVersion();
  const { activeWorkspace } = useWorkspaces();
  const { activeName: workspaceApiName, setActiveName } = useWorkspaceContext();

  // Deep-link support: on the very first render, if the URL contains a
  // workspace segment that differs from the default active workspace, sync
  // the context to the URL. This runs only once so it doesn't interfere
  // with user-initiated workspace switches (which change activeName first
  // and let page-level effects handle the URL update).
  const didSyncFromUrl = useRef(false);
  const initialUrlWorkspace = useRef(
    (() => {
      const match = /^\/workspaces\/([^/]+)(?:\/|$)/.exec(pathname);
      if (!match?.[1]) return null;
      try {
        return decodeURIComponent(match[1]);
      } catch {
        return match[1];
      }
    })(),
  );

  const needsInitialSync =
    !didSyncFromUrl.current &&
    !!initialUrlWorkspace.current &&
    initialUrlWorkspace.current !== workspaceApiName;

  useEffect(() => {
    if (didSyncFromUrl.current) return;
    didSyncFromUrl.current = true;
    if (
      initialUrlWorkspace.current &&
      initialUrlWorkspace.current !== workspaceApiName
    ) {
      setActiveName(initialUrlWorkspace.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const workspaceName =
    activeWorkspace?.displayName ?? activeWorkspace?.name ?? null;

  const navSections = useMemo(
    () => buildNavSections(workspaceName, workspaceApiName),
    [workspaceName, workspaceApiName],
  );

  // Reset the ErrorBoundary when the active page changes — but NOT on every
  // pathname change. `matches[1]` is the page-level route
  const pageKey = matches[1]?.id ?? pathname;

  return (
    <AppShell
      brandName="AI Studio"
      navSections={navSections}
      selectedPath={pathname}
      workspaceName={workspaceName}
      environment={config.appEnvironment}
      webVersion={config.appVersion}
      serviceVersion={serviceVersion.data?.version}
      isServiceVersionLoading={serviceVersion.isLoading}
      headerRight={
        <>
          <WorkspaceSwitcher />
          {account?.name && (
            <UserMenu
              displayName={account.name}
              onSignOut={() => void signOut()}
            />
          )}
        </>
      }
    >
      <RouteTelemetryTracker />
      {needsInitialSync ? (
        <LoadingSpinner label="Loading workspace..." />
      ) : (
        <ErrorBoundary key={pageKey}>
          <Outlet />
        </ErrorBoundary>
      )}
      <WorkspaceDrawer />
    </AppShell>
  );
}

function App() {
  const location = useLocation();
  const authenticationRequest = useMemo<RedirectRequest>(
    () => ({
      ...loginRequest,
      state: createAuthRedirectState(location),
    }),
    [location],
  );

  return (
    <>
      <MsalAuthenticationTemplate
        interactionType={InteractionType.Redirect}
        authenticationRequest={authenticationRequest}
        loadingComponent={AuthLoadingSpinner}
        errorComponent={AuthErrorMessage}
      >
        <HttpClientProvider>
          <WorkspaceProvider>
            <WorkspaceDrawerProvider>
              <AppContent />
            </WorkspaceDrawerProvider>
          </WorkspaceProvider>
        </HttpClientProvider>
      </MsalAuthenticationTemplate>
      <UnauthenticatedTemplate>
        <AuthLoadingSpinner />
      </UnauthenticatedTemplate>
    </>
  );
}

export default App;
